/**
 * Shared helpers for free challenge landing pages.
 *
 * WHY: slug matching must keep old campaign and email links working across
 * legacy challenge names without inventing dates.
 */

export const AI_TRADING_CHALLENGE_NAME = '7-Day AI Trading Challenge';

export const toChallengeSlug = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Normalize all known public names so old email links still resolve. */
export const normalizeChallengeSlug = (value) => {
  let slug = toChallengeSlug(value)
    .replace(/ai-stock-dashboard(?:-challenge)?/g, '7-day-ai-trading-challenge')
    .replace(/ai-stock-analyst-build-week/g, '7-day-ai-trading-challenge');
  if (!slug.includes('7-day-ai-trading-challenge')) {
    slug = slug.replace(/ai-trading-challenge/g, '7-day-ai-trading-challenge');
  }
  return slug;
};

/** Display stored names exactly. Legacy normalization belongs only in URL lookup. */
export const canonicalChallengeRunName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return AI_TRADING_CHALLENGE_NAME;
  return raw;
};

export const isChallengeReplayAvailable = (session, now = new Date()) => {
  if (!session?.recordingUrl || !session?.scheduledAt) return false;
  const scheduledAt = new Date(session.scheduledAt);
  return !Number.isNaN(scheduledAt.getTime()) && scheduledAt <= now;
};

export const canonicalChallengeSessionTitle = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return AI_TRADING_CHALLENGE_NAME;
  return raw;
};

export const challengeSlugMatches = (groupName, wantedSlug) => {
  const wanted = normalizeChallengeSlug(wantedSlug);
  const asSlug = normalizeChallengeSlug(groupName);
  if (!wanted || !asSlug) return false;
  return asSlug === wanted || asSlug.includes(wanted) || wanted.includes(asSlug);
};

export const selectChallengeRun = (groups, wantedSlug, now = new Date()) => {
  const matches = (Array.isArray(groups) ? groups : []).filter((group) =>
    challengeSlugMatches(group?.name, wantedSlug),
  );
  const wanted = normalizeChallengeSlug(wantedSlug);
  const exact = matches.find((group) => normalizeChallengeSlug(group?.name) === wanted);
  if (exact) return exact;

  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  const dated = matches
    .map((group) => ({ group, scheduledAt: new Date(group?.scheduledAt).getTime() }))
    .filter(({ scheduledAt }) => Number.isFinite(scheduledAt));
  const upcoming = dated
    .filter(({ scheduledAt }) => scheduledAt > nowMs)
    .sort((left, right) => left.scheduledAt - right.scheduledAt)[0];
  if (upcoming) return upcoming.group;
  return dated.sort((left, right) => right.scheduledAt - left.scheduledAt)[0]?.group || matches[0];
};

export const splitDisplayName = (fullName) => {
  const cleaned = String(fullName || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!cleaned) return { firstName: '', lastName: '' };
  const parts = cleaned.split(' ');
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

export const formatChallengeDate = (value, options = {}) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    weekday: options.weekday === false ? undefined : 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
    ...options,
  });
};

export const formatChallengeDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
  return `${day} · ${time}`;
};

export const pickKickoffAndDemo = (sessions = []) => {
  const sorted = [...sessions].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  return {
    kickoff: sorted[0] || null,
    demoDay: sorted.length > 1 ? sorted[sorted.length - 1] : null,
    sessions: sorted,
  };
};

export const youtubeLiveUrl = (session) =>
  session?.materials?.find(
    (material) => material?.type === 'youtube_live' && /^https:\/\//i.test(material?.url || ''),
  )?.url || null;

const compactUtc = (value) =>
  new Date(value)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d+Z$/, 'Z');

export const buildChallengeCalendarLinks = (session, apiBaseUrl) => {
  if (!session?.scheduledAt) return null;
  const start = new Date(session.scheduledAt);
  if (Number.isNaN(start.getTime())) return null;
  const parsedDuration =
    Number(session.durationMinutes) ||
    Number.parseInt(String(session.duration || '').match(/\d+/)?.[0] || '60', 10);
  const end = new Date(start.getTime() + parsedDuration * 60 * 1000);
  const title = canonicalChallengeSessionTitle(session.title);
  const location = session.joinUrl || '';
  const description = location ? `${title}. Join: ${location}` : title;
  const apiBase = String(apiBaseUrl || 'https://api.botspot.trade').replace(/\/+$/, '');
  const googleParams = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${compactUtc(start)}/${compactUtc(end)}`,
    details: description,
    location,
    ctz: 'America/New_York',
  });
  const icsParams = new URLSearchParams({
    title,
    start: start.toISOString(),
    end: end.toISOString(),
    desc: description,
    loc: location,
    tz: 'America/New_York',
  });

  return {
    google: `https://calendar.google.com/calendar/render?${googleParams.toString()}`,
    ics: `${apiBase}/courses/calendar/ics?${icsParams.toString()}`,
  };
};

// Lawsuit-risk ban 2026-08-08: do NOT reintroduce a second customer SMS
// opt-in helper or any partial/event-scoped disclosure. Customer SMS opt-in
// on this page is ONLY shared SmsConsent / buildSmsConsentPayload ->
// body.smsMarketingConsent.

const parseCatalogDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

export const formatBootcampOfferDate = (value) =>
  parseCatalogDate(value)?.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  }) || null;

/**
 * Reads the Bootcamp's live cohort date and current tuition window from the
 * checkout catalog. Shared by every post-registration challenge page so the
 * Bootcamp mention on each one comes from the same database facts, never a
 * hardcoded date or price (SPOT WEBSITE CONTEXT MUST BE DYNAMIC).
 */
export const fetchBootcampOfferSummary = async (fetchCatalogFn) => {
  const catalog = await fetchCatalogFn();
  const product = catalog?.products?.find((entry) => entry.slug === 'ai-bootcamp');
  const startsAt = parseCatalogDate(product?.metadata?.cohort?.startsAt);
  const currentWindow = catalog?.tuitionSchedule?.windows?.find((window) => window.isCurrent);
  const windowEnd = parseCatalogDate(currentWindow?.endDate);
  return {
    name: product?.name || catalog?.tuitionSchedule?.cohortName || null,
    cohortName: catalog?.tuitionSchedule?.cohortName || product?.metadata?.cohort?.name || null,
    startsAt: startsAt && startsAt > new Date() ? startsAt : null,
    tuitionWindow:
      currentWindow?.tier !== 'full_price' && windowEnd && windowEnd > new Date()
        ? currentWindow
        : null,
  };
};
